import MapboxDraw from '@mapbox/mapbox-gl-draw';

const Constants = MapboxDraw.constants;
const { CommonSelectors, doubleClickZoom } = MapboxDraw.lib;
const defaultDrawPolygon = MapboxDraw.modes.draw_polygon;

/** Click-drag axis-aligned rectangle (LngLat box → GeoJSON Polygon). Pairs with FreehandMode on `draw_polygon`. */
const RectangleMode = {
  onSetup() {
    const polygon = this.newFeature({
      type: Constants.geojsonTypes.FEATURE,
      properties: {},
      geometry: {
        type: Constants.geojsonTypes.POLYGON,
        coordinates: [[]],
      },
    });

    this.addFeature(polygon);
    this.clearSelectedFeatures();
    if (doubleClickZoom) {
      doubleClickZoom.disable(this);
    }
    
    // Disable dragPan on setup so click-drag drawing works immediately
    if (this.map?.dragPan) {
      this.map.dragPan.disable();
    }

    this.updateUIClasses({ mouse: Constants.cursors.ADD });
    this.activateUIButton(Constants.types.POLYGON);
    this.setActionableState({ trash: true });

    return {
      polygon,
      isDragging: false,
      startLngLat: null,
      committed: false,
    };
  },

  onMouseDown: onPointerDown,
  onTouchStart: onPointerDown,
  onDrag: onPointerMove,
  onMouseMove: onPointerMove,
  onTouchMove: onPointerMove,
  onMouseUp: onPointerUp,
  onTouchEnd: onPointerUp,
  onClick: onPointerUp,
  onTap: onPointerUp,

  onKeyUp(state, e) {
    if (CommonSelectors.isEscapeKey(e)) {
      if (this.map?.dragPan) {
        this.map.dragPan.enable();
      }
      state.isDragging = false;
      state.startLngLat = null;
      this.deleteFeature([state.polygon.id], { silent: true });
      this.changeMode(Constants.modes.SIMPLE_SELECT);
    }
  },

  onTrash(state) {
    if (this.map?.dragPan) {
      this.map.dragPan.enable();
    }
    state.isDragging = false;
    this.deleteFeature([state.polygon.id], { silent: true });
    this.changeMode(Constants.modes.SIMPLE_SELECT);
  },

  onStop(state) {
    this.updateUIClasses({ mouse: Constants.cursors.NONE });
    if (doubleClickZoom) {
      doubleClickZoom.enable(this);
    }
    this.activateUIButton();

    if (this.map?.dragPan) {
      this.map.dragPan.enable();
    }

    if (state?.polygon?.id && !state.committed && this.getFeature(state.polygon.id)) {
      this.deleteFeature([state.polygon.id], { silent: true });
    }
  },

  toDisplayFeatures: defaultDrawPolygon.toDisplayFeatures,
};

function onPointerDown(state, e) {
  if (CommonSelectors.isVertex(e)) return;
  const oe = e.originalEvent;
  if (oe && 'button' in oe && typeof oe.button === 'number' && oe.button !== 0) return;

  state.isDragging = true;
  state.startLngLat = e.lngLat;
  state.committed = false;
}

function onPointerMove(state, e) {
  if (!state.isDragging || !state.startLngLat) return;

  const a = state.startLngLat;
  const b = e.lngLat;
  const west = Math.min(a.lng, b.lng);
  const east = Math.max(a.lng, b.lng);
  const south = Math.min(a.lat, b.lat);
  const north = Math.max(a.lat, b.lat);

  state.polygon.setCoordinates([
    [
      [west, south],
      [east, south],
      [east, north],
      [west, north],
      [west, south],
    ],
  ]);
}

function onPointerUp(state) {
  if (!state.isDragging) {
    if (!state.committed) {
      this.deleteFeature([state.polygon.id], { silent: true });
      this.changeMode(Constants.modes.SIMPLE_SELECT);
    }
    return;
  }
  state.isDragging = false;
  const start = state.startLngLat;
  state.startLngLat = null;

  if (this.map?.dragPan) {
    this.map.dragPan.enable();
  }

  if (!start) {
    this.deleteFeature([state.polygon.id], { silent: true });
    this.changeMode(Constants.modes.SIMPLE_SELECT);
    return;
  }

  const ring = state.polygon.coordinates[0];
  if (!ring || ring.length < 4) {
    this.deleteFeature([state.polygon.id], { silent: true });
    this.changeMode(Constants.modes.SIMPLE_SELECT);
    return;
  }

  const west = ring[0][0];
  const south = ring[0][1];
  const east = ring[2][0];
  const north = ring[2][1];

  if (Math.abs(east - west) < 1e-12 || Math.abs(north - south) < 1e-12) {
    this.deleteFeature([state.polygon.id], { silent: true });
    this.changeMode(Constants.modes.SIMPLE_SELECT);
    return;
  }

  state.committed = true;
  this.fire(Constants.events.CREATE, {
    features: [state.polygon.toGeoJSON()],
  });

  this.changeMode(Constants.modes.SIMPLE_SELECT, { featureIds: [state.polygon.id] });
}

export default RectangleMode;
